<template>
  <div id="CampCart">
    <div class="headerbg">
      <b-container class="d-flex align-items-center justify-content-between">
        <h1 class="cart-title">
          營隊購物車
          <b-badge
            v-if="items.length"
            pill
            variant="light"
            class="cart-count-badge"
            >{{ items.length }}</b-badge
          >
        </h1>
        <b-button
          class="text-nowrap"
          variant="outline-secondary"
          size="sm"
          :to="{ name: 'cart' }"
        >
          <b-icon-arrow-left-right class="mr-1"></b-icon-arrow-left-right
          ><strong>一般課程</strong> ({{ courseCartCount }})
        </b-button>
      </b-container>
    </div>
    <b-container>
      <b-row v-if="hasSoldOut" class="mb-3">
        <b-col cols="12">
          <b-alert show variant="danger">
            以下項目已額滿，請移除後再前往報名：{{ soldOutNames }}
          </b-alert>
        </b-col>
      </b-row>

      <b-row class="mb-3">
        <b-col cols="12">
          <b-card class="mb-3">
            <b-row class="d-none d-md-flex flex-wrap p-0">
              <b-col cols="1"></b-col>
              <b-col cols="4" class="font-weight-bold text-nowrap"
                >營隊梯次</b-col
              >
              <b-col cols="2" class="font-weight-bold text-nowrap"
                >學員姓名</b-col
              >
              <b-col cols="2" class="font-weight-bold text-nowrap">日期</b-col>
              <b-col cols="2" class="font-weight-bold text-nowrap">金額</b-col>
              <b-col cols="1" class="font-weight-bold text-center text-nowrap"
                >操作</b-col
              >
              <b-col cols="12"><hr /></b-col>
            </b-row>

            <template v-if="loading">
              <b-row
                ><b-col class="text-center py-3"><b-spinner></b-spinner></b-col
              ></b-row>
            </template>

            <template v-else-if="mainItems.length === 0">
              <b-alert show variant="primary">購物車目前沒有東西唷！</b-alert>
            </template>

            <template v-else>
              <template v-for="(item, index) in mainItems">
                <div :key="item.Id" class="cart-item-block">
                  <!-- 桌面版：欄位式 -->
                  <b-row
                    class="d-none d-md-flex flex-wrap p-0 align-items-center"
                  >
                    <b-col cols="1" class="text-center px-0">
                      <b-form-checkbox
                        v-model="selectedIds"
                        :value="item.Id"
                        :disabled="item.HasConflict || item.IsSoldOut"
                      />
                    </b-col>
                    <b-col cols="4">
                      <div>
                        {{ item.CampProductTitle }}
                        <b-badge
                          v-if="item.IsCampAddon"
                          variant="info"
                          class="ml-1"
                          >加購</b-badge
                        >
                      </div>
                      <div class="small text-muted">{{ item.CampProductName }}</div>
                      <b-badge
                        v-if="item.IsSoldOut"
                        variant="danger"
                        class="ml-1"
                        >額滿</b-badge
                      >
                      <b-badge
                        v-if="item.HasConflict"
                        variant="danger"
                        class="ml-1"
                        >衝堂</b-badge
                      >
                      <div v-if="item.IsSoldOut" class="small text-danger mt-1">
                        「{{ productLabel(item) }}」已額滿，請移除後再前往報名
                      </div>
                    </b-col>
                    <b-col cols="2">{{ item.RelationName }}</b-col>
                    <b-col cols="2" class="small">
                      {{ item.Start | datefmt("MM/DD") }} ~
                      {{ item.End | datefmt("MM/DD") }}
                    </b-col>
                    <b-col cols="2">$ {{ item.Price | money(0, 2) }}</b-col>
                    <b-col cols="1" class="text-center px-0">
                      <button
                        type="button"
                        class="cart-item-delete-icon"
                        aria-label="刪除"
                        @click="deleteItem(item.Id)"
                      >
                        <b-icon icon="trash"></b-icon>
                      </button>
                    </b-col>
                  </b-row>

                  <!-- 手機版：卡片式 -->
                  <div class="d-md-none cart-item-card">
                    <div class="cart-item-card-top">
                      <b-form-checkbox
                        v-model="selectedIds"
                        :value="item.Id"
                        :disabled="item.HasConflict || item.IsSoldOut"
                        class="cart-item-checkbox"
                      />
                      <div class="cart-item-card-title">
                        <div class="font-weight-bold">
                          {{ item.CampProductTitle }}
                          <b-badge
                            v-if="item.IsCampAddon"
                            variant="info"
                            class="ml-1"
                            >加購</b-badge
                          >
                          <b-badge
                            v-if="item.IsSoldOut"
                            variant="danger"
                            class="ml-1"
                            >額滿</b-badge
                          >
                          <b-badge
                            v-if="item.HasConflict"
                            variant="danger"
                            class="ml-1"
                            >衝堂</b-badge
                          >
                        </div>
                        <div class="small text-muted">{{ item.CampProductName }}</div>
                        <div v-if="item.IsSoldOut" class="small text-danger mt-1">
                          此梯次已額滿，請移除後再前往報名
                        </div>
                      </div>
                      <button
                        type="button"
                        class="cart-item-delete-icon"
                        aria-label="刪除"
                        @click="deleteItem(item.Id)"
                      >
                        <b-icon icon="trash"></b-icon>
                      </button>
                    </div>
                    <div class="cart-item-card-meta">
                      <div class="cart-item-card-line">
                        <span class="cart-item-label">學員姓名</span>
                        <span>{{ item.RelationName }}</span>
                      </div>
                      <div class="cart-item-card-line">
                        <span class="cart-item-label">日期</span>
                        <span
                          >{{ item.Start | datefmt("MM/DD") }} ~
                          {{ item.End | datefmt("MM/DD") }}</span
                        >
                      </div>
                      <div class="cart-item-card-line">
                        <span class="cart-item-label">金額</span>
                        <span class="cart-item-price"
                          >$ {{ item.Price | money(0, 2) }}</span
                        >
                      </div>
                    </div>
                  </div>

                  <template
                    v-if="
                      addonsBySourceCartId[item.Id] &&
                        addonsBySourceCartId[item.Id].length
                    "
                  >
                    <b-row class="flex-wrap p-0">
                      <b-col cols="1" class="d-none d-md-block"></b-col>
                      <b-col cols="12" md="11" class="mt-3">
                        <div class="camp-addon camp-addon-selected">
                          <button
                            type="button"
                            class="camp-addon-toggle"
                            @click="toggleSelectedAddon(item.Id)"
                          >
                            <span class="camp-addon-toggle-main">
                              <span class="camp-addon-pill camp-addon-pill-selected"
                                >已加購</span
                              >
                              <span class="camp-addon-count"
                                >{{ addonsBySourceCartId[item.Id].length }} 項</span
                              >
                            </span>
                            <span class="camp-addon-toggle-text">
                              {{
                                isSelectedAddonOpen(item.Id) ? "收合" : "展開查看"
                              }}
                              <b-icon
                                :icon="
                                  isSelectedAddonOpen(item.Id)
                                    ? 'chevron-up'
                                    : 'chevron-down'
                                "
                              ></b-icon>
                            </span>
                          </button>
                          <b-collapse :visible="isSelectedAddonOpen(item.Id)">
                            <div
                              v-for="addon in addonsBySourceCartId[item.Id]"
                              :key="addon.Id"
                              class="camp-addon-item"
                            >
                              <div class="camp-addon-info">
                                <div class="font-weight-bold">
                                  {{ addon.CampProductTitle }}
                                  <b-badge variant="info" class="ml-1"
                                    >加購</b-badge
                                  >
                                  <b-badge
                                    v-if="addon.IsSoldOut"
                                    variant="danger"
                                    class="ml-1"
                                    >額滿</b-badge
                                  >
                                </div>
                                <div class="small text-muted">
                                  {{ addon.CampProductName }}
                                  <span v-if="addon.Start"
                                    >｜{{ addon.Start | datefmt("MM/DD") }} ~
                                    {{ addon.End | datefmt("MM/DD") }}</span
                                  >
                                </div>
                                <div v-if="addon.IsSoldOut" class="small text-danger mt-1">
                                  「{{ productLabel(addon) }}」加購已額滿，請移除後再前往報名
                                </div>
                              </div>
                              <div class="camp-addon-action">
                                <div class="camp-addon-price">
                                  $ {{ addon.Price | money(0, 2) }}
                                </div>
                                <button
                                  type="button"
                                  class="cart-item-delete-icon"
                                  aria-label="刪除"
                                  @click="deleteItem(addon.Id)"
                                >
                                  <b-icon icon="trash"></b-icon>
                                </button>
                              </div>
                            </div>
                          </b-collapse>
                        </div>
                      </b-col>
                    </b-row>
                  </template>

                  <template v-if="showAddonOptions(item.Id)">
                    <b-row class="flex-wrap p-0">
                      <b-col cols="1" class="d-none d-md-block"></b-col>
                      <b-col cols="12" md="11" class="mt-3">
                        <div class="camp-addon">
                          <button
                            type="button"
                            class="camp-addon-toggle"
                            @click="toggleAddonOptions(item.Id)"
                          >
                            <span class="camp-addon-toggle-main">
                              <span class="camp-addon-pill">可加購</span>
                              <span class="camp-addon-count"
                                >{{ addonOptionCount(item.Id) }} 項可選</span
                              >
                            </span>
                            <span class="camp-addon-toggle-text">
                              {{
                                isAddonOptionOpen(item.Id) ? "收合" : "展開查看"
                              }}
                              <b-icon
                                :icon="
                                  isAddonOptionOpen(item.Id)
                                    ? 'chevron-up'
                                    : 'chevron-down'
                                "
                              ></b-icon>
                            </span>
                          </button>
                          <b-collapse :visible="isAddonOptionOpen(item.Id)">
                            <div
                              v-for="option in availableAddonOptions(item.Id)"
                              :key="option.CampAddonRuleItemId"
                              class="camp-addon-item"
                            >
                              <div class="camp-addon-info">
                                <div class="font-weight-bold">
                                  {{ addonOptionTitle(option) }}
                                </div>
                                <div class="small text-muted">
                                  {{ option.CampProductName }}
                                  <span v-if="option.Start"
                                    >｜{{ option.Start | datefmt("MM/DD") }} ~
                                    {{ option.End | datefmt("MM/DD") }}</span
                                  >
                                </div>
                              </div>
                              <div class="camp-addon-action">
                                <div class="camp-addon-price">
                                  $ {{ option.AddonPrice | money(0, 2) }}
                                </div>
                                <b-button
                                  size="sm"
                                  variant="outline-primary"
                                  :disabled="
                                    addingAddonId === option.CampAddonRuleItemId
                                  "
                                  @click="addAddon(item.Id, option)"
                                >
                                  {{
                                    addingAddonId === option.CampAddonRuleItemId
                                      ? "加入中..."
                                      : "加入加購"
                                  }}
                                </b-button>
                              </div>
                            </div>
                          </b-collapse>
                        </div>
                      </b-col>
                    </b-row>
                  </template>

                  <hr
                    v-if="index + 1 !== mainItems.length"
                    class="cart-item-divider"
                  />
                </div>
              </template>

              <b-col
                cols="12"
                style="height:2px;background:#000"
                class="my-3"
              ></b-col>
              <b-col cols="12" class="text-right">
                已選小計：$ {{ selectedTotalPrice | money(0, 2) }}
              </b-col>
            </template>
          </b-card>
        </b-col>
      </b-row>

      <b-row v-if="mainItems.length > 0">
        <b-col cols="12">
          <b-alert v-if="hasConflict" show variant="danger">
            購物車中有衝堂項目，請移除後再前往報名。
          </b-alert>
        </b-col>
        <b-col cols="12" class="d-flex justify-content-end mb-5">
          <b-button
            variant="primary"
            :disabled="selectedIds.length === 0"
            @click="toCheckout"
            >前往報名</b-button
          >
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import { apiCheckMember } from "@/api/API.js";

export default {
  name: "CampCart",
  data() {
    return {
      addingAddonId: null,
      openAddonOptionIds: {},
      openSelectedAddonIds: {},
    };
  },
  computed: {
    items() {
      return this.$store.state.campCart.items;
    },
    courseCartCount() {
      return this.$store.state.courseCart.count;
    },
    mainItems() {
      return this.$store.getters["campCart/mainItems"];
    },
    loading() {
      return this.$store.state.campCart.loading;
    },
    hasConflict() {
      return this.$store.getters["campCart/hasConflict"];
    },
    hasSoldOut() {
      return this.$store.getters["campCart/hasSoldOut"];
    },
    soldOutNames() {
      const names = this.items
        .filter((i) => i.IsSoldOut)
        .map((i) => this.productLabel(i))
        .filter(Boolean);
      return [...new Set(names)].join("、");
    },
    selectedTotalPrice() {
      return this.$store.getters["campCart/selectedTotalPrice"];
    },
    addonOptionsByCartId() {
      return this.$store.getters["campCart/addonOptionsByCartId"];
    },
    addonsBySourceCartId() {
      return this.$store.getters["campCart/addonsBySourceCartId"];
    },
    visibleAddonOptionSourceIds() {
      const shownRelationIds = new Set();

      return this.mainItems.reduce((map, item) => {
        const relationKey = item.RelationId || item.Id;
        const addonOptions = this.addonOptionsByCartId[item.Id] || [];

        const hasAvailableAddon = addonOptions.some(
          (option) => !option.DisabledReason,
        );

        if (
          addonOptions.length > 0 &&
          hasAvailableAddon &&
          !shownRelationIds.has(relationKey)
        ) {
          map[item.Id] = true;
          shownRelationIds.add(relationKey);
        }

        return map;
      }, {});
    },
    selectedIds: {
      get() {
        return this.$store.state.campCart.selectedIds;
      },
      set(val) {
        this.$store.commit("campCart/setSelectedIds", val);
      },
    },
  },
  mounted() {
    apiCheckMember()
      .then(() => {
        this.$store.dispatch("campCart/fetchCart").then(() => {
          if (this.hasSoldOut)
            alert(`以下項目已額滿，請移除後再前往報名：${this.soldOutNames}`);
        });
        this.$store.dispatch("courseCart/fetchCount");
      })
      .catch(() => {
        alert("尚未登入");
        this._go("/");
      });
  },
  methods: {
    addAddon(sourceCartId, option) {
      this.addingAddonId = option.CampAddonRuleItemId;
      this.$store
        .dispatch("campCart/addAddon", {
          SourceCartId: sourceCartId,
          CampAddonRuleItemId: option.CampAddonRuleItemId,
        })
        .catch((err) => {
          alert(err?.response?.data?.error || "加入加購商品失敗，請稍後再試");
        })
        .finally(() => {
          this.addingAddonId = null;
        });
    },
    deleteItem(id) {
      if (!confirm("確定移除此項目？")) return;
      this.$store.dispatch("campCart/removeItem", id);
    },
    showAddonOptions(sourceCartId) {
      return !!this.visibleAddonOptionSourceIds[sourceCartId];
    },
    availableAddonOptions(sourceCartId) {
      return (this.addonOptionsByCartId[sourceCartId] || []).filter(
        (option) => !option.DisabledReason,
      );
    },
    addonOptionCount(sourceCartId) {
      return this.availableAddonOptions(sourceCartId).length;
    },
    isAddonOptionOpen(sourceCartId) {
      return !!this.openAddonOptionIds[sourceCartId];
    },
    toggleAddonOptions(sourceCartId) {
      this.$set(
        this.openAddonOptionIds,
        sourceCartId,
        !this.openAddonOptionIds[sourceCartId],
      );
    },
    isSelectedAddonOpen(sourceCartId) {
      return !!this.openSelectedAddonIds[sourceCartId];
    },
    toggleSelectedAddon(sourceCartId) {
      this.$set(
        this.openSelectedAddonIds,
        sourceCartId,
        !this.openSelectedAddonIds[sourceCartId],
      );
    },
    addonOptionTitle(option) {
      const programName = option.CampProgramName || "";
      const productTitle =
        option.CampProductTitle || option.CampProductName || "";
      return programName && productTitle && programName !== productTitle
        ? `${programName} ${productTitle}`
        : productTitle || programName;
    },
    // CampProductTitle 是所屬課程系列名稱（CampProgram.Name），CampProductName 才是實際梯次名稱
    // （CampProduct.Title）；額滿提示需要指名是哪個梯次，兩者都有時合併顯示避免混淆。
    productLabel(item) {
      const seriesName = item.CampProductTitle || "";
      const productName = item.CampProductName || "";
      return seriesName && productName && seriesName !== productName
        ? `${seriesName}－${productName}`
        : productName || seriesName;
    },
    toCheckout() {
      if (this.hasSoldOut) {
        alert(`以下項目已額滿，請移除後再前往報名：${this.soldOutNames}`);
        return;
      }
      this._go("/campCheckout");
    },
  },
};
</script>

<style scoped>
.cart-title {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.8rem;
  white-space: nowrap;
}

.cart-count-badge {
  border: 1px solid currentColor;
  background-color: #fff !important;
  color: #007ac2;
  font-size: 0.85rem;
  line-height: 1;
}

.camp-addon {
  border-left: 3px solid #1d6fb8;
  background: #f7fbff;
  padding: 12px 14px;
}

.camp-addon-selected {
  border-left-color: #2f855a;
  background: #f5fff8;
}

.camp-addon-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 42px;
  padding: 0.35rem 0;
  border: 0;
  background: transparent;
  color: #184e77;
  text-align: left;
}

.camp-addon-toggle:focus {
  outline: 0;
}

.camp-addon-toggle:focus-visible {
  outline: 2px solid rgba(29, 111, 184, 0.35);
  outline-offset: 3px;
  border-radius: 6px;
}

.camp-addon-toggle-main {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  min-width: 0;
}

.camp-addon-pill {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  background-color: #dceeff;
  color: #0f5f9e;
  font-size: 0.86rem;
  font-weight: 700;
  line-height: 1.2;
}

.camp-addon-pill-selected {
  background-color: #dcf5e3;
  color: #2f855a;
}

.camp-addon-count {
  color: #24465f;
  font-size: 0.95rem;
  font-weight: 700;
}

.camp-addon-toggle-text {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
  color: #1d6fb8;
  font-size: 0.88rem;
  font-weight: 700;
}

.camp-addon-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-top: 1px solid #d8e8f5;
}


.camp-addon-item:first-of-type {
  border-top: 0;
  padding-top: 0;
}

.camp-addon-info {
  min-width: 0;
}

.camp-addon-action {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.camp-addon-price {
  color: #c04b2f;
  font-weight: 700;
  white-space: nowrap;
}

@media (max-width: 767.98px) {
  .camp-addon-item {
    align-items: flex-start;
    flex-direction: column;
  }

  .camp-addon-action {
    width: 100%;
    justify-content: space-between;
  }
}

@media (max-width: 799.98px) {
  .cart-title {
    font-size: 1.6rem;
  }
}

.cart-item-block {
  padding: 0.75rem 0;
}

.cart-item-divider {
  margin: 0;
}

.cart-item-card-top {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.cart-item-checkbox {
  margin-top: 0.2rem;
}

.cart-item-delete-icon {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  background: transparent;
  color: #c04b2f;
  font-size: 1.1rem;
  border-radius: 4px;
}

.cart-item-delete-icon:active {
  background: #fbe9e7;
}

.cart-item-card-title {
  min-width: 0;
  flex: 1 1 auto;
}

.cart-item-card-meta {
  margin-top: 0.6rem;
  padding: 0.6rem 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
}

.cart-item-card-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
}

.cart-item-card-line + .cart-item-card-line {
  margin-top: 0.35rem;
}

.cart-item-label {
  color: #6c757d;
}

.cart-item-price {
  color: #c04b2f;
  font-weight: 700;
}
</style>
